import {
  Button,
  Card,
  CardProps,
  Classes,
  Menu,
  MenuItem,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import {
  TEST_IDS,
  getProjectResourceMenuButtonTestId,
} from 'src/constants/testIds';
import { useRemoveResource } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResource';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { projectFileUploadSelector } from 'src/selectors/projectFileUpload';
import { projectModelSelector } from 'src/selectors/projectModel';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectResourceCardProps = {
  cardProps?: CardProps;
  header?: React.ReactNode;
  text: string;
  type: string;
};

const ProjectResourceCard: React.VFC<ProjectResourceCardProps> = ({
  cardProps = {},
  header,
  text,
  type,
}) => (
  <Card
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...cardProps}
    className={classNames(
      'sd-project-resource-card',
      Classes.TEXT_SMALL,
      cardProps.className
    )}
    elevation={2}
  >
    {header}
    <div className={classNames('sd-project-resource-card__details')}>
      <div
        className="sd-project-resource-card__text"
        data-testid={TEST_IDS.projectSidebar.resourceCardText}
      >
        <Text ellipsize>{text}</Text>
      </div>
      <div
        className={classNames(
          'sd-project-resource-card__type',
          Classes.TEXT_DISABLED
        )}
      >
        {type}
      </div>
    </div>
  </Card>
);

const useProjectFileUpload = (fileId: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectFileUploadSelector(projectId, fileId));
};

type ProjectResourceFileUploadCardProps = {
  idx: number | undefined;
  fileId: string;
};

export const ProjectResourceFileUploadCard: React.VFC<
  ProjectResourceFileUploadCardProps
> = ({ idx, fileId }) => {
  const dispatch = useReduxDispatch();
  const fileUpload = useProjectFileUpload(fileId);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleDeleteFileUpload = useRemoveResource();

  const onMouseEnter = React.useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  const onMouseLeave = React.useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  const header = (
    <div className={Classes.HEADING}>
      <Text ellipsize>{fileUpload?.name || fileId}</Text>
      <Popover2
        content={
          <Menu>
            <MenuItem
              data-testid={
                TEST_IDS.projectSidebar.resourceMenu.viewPreviewButton
              }
              onClick={() => {
                dispatch(
                  uiSlice.actions.toggledFileUploadPreviewDialog({
                    isOpen: true,
                    resourceId: fileId,
                  })
                );
              }}
              text={copy.components.project.resources.viewPreview}
            />
            <MenuItem
              data-testid={TEST_IDS.projectSidebar.resourceMenu.deleteButton}
              onClick={() =>
                handleDeleteFileUpload({ key: getResourceKey(fileId) })
              }
              text={copy.components.project.resources.delete}
            />
          </Menu>
        }
        // will not close on canvas click, so a backdrop is necessary
        hasBackdrop
        isOpen={isMenuOpen}
        minimal
        onClose={() => {
          setIsMenuOpen(false);
        }}
        targetProps={{
          onClick: (e) => {
            // prevent triggering parent interaction
            e.preventDefault();
          },
        }}
      >
        <Button
          active={isMenuOpen}
          data-testid={getProjectResourceMenuButtonTestId(fileId, idx)}
          icon={isHovered || isMenuOpen ? IconNames.MORE : IconNames.BLANK}
          minimal
          onClick={() => {
            setIsMenuOpen(!isMenuOpen);
          }}
        />
      </Popover2>
    </div>
  );

  return (
    <ProjectResourceCard
      cardProps={{
        onMouseEnter,
        onMouseLeave,
      }}
      header={header}
      text={fileUpload?.path || fileId}
      type={copy.components.project.resourceTypes.fileUpload}
    />
  );
};

const useProjectVirtualGraph = (virtualGraphId: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(
    projectVirtualGraphSelector(projectId, virtualGraphId)
  );
};

type ProjectResourceVirtualGraphCardProps = {
  idx: number | undefined;
  virtualGraphId: string;
};

export const ProjectResourceVirtualGraphCard: React.VFC<
  ProjectResourceVirtualGraphCardProps
> = ({ idx, virtualGraphId }) => {
  const dispatch = useReduxDispatch();
  const virtualGraph = useProjectVirtualGraph(virtualGraphId);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleDeleteVirtualGraph = useRemoveResource();

  const onMouseEnter = React.useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  const onMouseLeave = React.useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  const header = (
    <div className={Classes.HEADING}>
      <Text ellipsize>{virtualGraph?.name || virtualGraphId}</Text>
      <Popover2
        content={
          <Menu>
            <MenuItem
              data-testid={
                TEST_IDS.projectSidebar.resourceMenu.viewPreviewButton
              }
              onClick={() => {
                dispatch(
                  uiSlice.actions.toggledVirtualGraphPreviewDialog({
                    isOpen: true,
                    resourceId: virtualGraphId,
                  })
                );
              }}
              text={copy.components.project.resources.viewPreview}
            />
            <MenuItem
              data-testid={TEST_IDS.projectSidebar.resourceMenu.deleteButton}
              onClick={() =>
                handleDeleteVirtualGraph({
                  key: getResourceKey(virtualGraphId),
                })
              }
              text={copy.components.project.resources.delete}
            />
          </Menu>
        }
        // will not close on canvas click, so a backdrop is necessary
        hasBackdrop
        isOpen={isMenuOpen}
        minimal
        onClose={() => {
          setIsMenuOpen(false);
        }}
        targetProps={{
          onClick: (e) => {
            // prevent triggering parent interaction
            e.preventDefault();
          },
        }}
      >
        <Button
          active={isMenuOpen}
          data-testid={getProjectResourceMenuButtonTestId(virtualGraphId, idx)}
          icon={isHovered || isMenuOpen ? IconNames.MORE : IconNames.BLANK}
          minimal
          onClick={() => {
            setIsMenuOpen(!isMenuOpen);
          }}
        />
      </Popover2>
    </div>
  );

  return (
    <ProjectResourceCard
      cardProps={{
        onMouseEnter,
        onMouseLeave,
      }}
      header={header}
      text={virtualGraph?.dataSourceId || ''}
      type={copy.components.project.resourceTypes.virtualGraph}
    />
  );
};

const useProjectModel = (id: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectModelSelector(projectId, id));
};

type ProjectResourceModelCardProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  idx: number | undefined;
  modelId: string;
};

export const ProjectResourceModelCard: React.VFC<
  ProjectResourceModelCardProps
> = ({ modelId }) => {
  const model = useProjectModel(modelId);
  return (
    <ProjectResourceCard
      text={model?.name || ''}
      type={copy.components.project.resourceTypes.model}
    />
  );
};
